import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { validateReportOutage } from "../../common/utils/validationRules";
import isEqual from 'lodash/isEqual';
import type { PayloadAction } from "@reduxjs/toolkit";
import { OutagesState, CustomAction, Outage } from "./types";
import { trackEvent } from "../../utils/analytics";

type initialStateType = {
    outagesState: OutagesState;
};

const initialState: initialStateType = {
    outagesState: {
        outages: [],
        outageParams: {
            outagePhoneNumber: null,
            meterNumber: null,
            region: null,
            county: null,
            area: null,
        },
        newOutage: null,
        loading: false,
        canSubmit: false,
        inputErrors: {
            outagePhoneNumber: null,
            meterNumber: null,
            region: null,
            county: null,
            area: null,
        },
        feedbackMessage: {
            message: null,
            isError: false,
            open: false,
        },
        resetOutageParams: false,
        showOutageDetails: false,
        searchResults: [],
        searchLoading: false,
        searchError: null,
    },
};

export const outagesSlice = createSlice({
    name: "outages",
    initialState,
    reducers: {
        setOutages: (state, action: PayloadAction<Outage[]>) => {
            state.outagesState.outages = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.outagesState.loading = action.payload;
        },
        setResetOutageParams: (state, action: PayloadAction<boolean>) => {
          state.outagesState.resetOutageParams = action.payload;
        },
        setInputErrors: (state, action: PayloadAction<any>) => {
            const errors = action.payload.errors;
            let newErrors = { ...state.outagesState.inputErrors, ...errors };
            if (!errors || Object.keys(errors).length === 0) {
                state.outagesState.inputErrors = initialState.outagesState.inputErrors;
                newErrors = initialState.outagesState.inputErrors;
            } else {
                state.outagesState.inputErrors = newErrors;
            }
            state.outagesState.canSubmit = Object.values(newErrors).every(error => !error) &&
                state.outagesState.outageParams != null &&
                Object.values(state.outagesState.outageParams).every(param => param !== null && param !== '');

        },
        setOutageParams: (state, action: PayloadAction<any>) => {
            if (action.payload.values) {
                state.outagesState.outageParams = {
                    ...state.outagesState.outageParams,
                    ...action.payload.values
                };
            }
            const errors = validateReportOutage(state.outagesState.outageParams);
            if (!isEqual(state.outagesState.inputErrors, errors)) {
                state.outagesState.inputErrors = { ...state.outagesState.inputErrors, ...errors };
            }
        },
        submitOutage: (state, action) => {
          if (action.payload.methodRequestState === 'requested') {
            state.outagesState.loading = true;
          } else if (action.payload.methodRequestState === 'finished') {
            if (action.payload.result) {
              state.outagesState.feedbackMessage = {
                message: 'Successfully reported the outage!',
                open: true,
                isError: false,
              };
              trackEvent('outage_reported', {
                outagePhoneNumber: state.outagesState.outageParams?.outagePhoneNumber,
                meterNumber: state.outagesState.outageParams?.meterNumber,
                region: state.outagesState.outageParams?.region,
                county: state.outagesState.outageParams?.county,
                area: state.outagesState.outageParams?.area,
              });
              state.outagesState.newOutage = initialState.outagesState.newOutage;
              state.outagesState.resetOutageParams = true;
              state.outagesState.outageParams = initialState.outagesState.outageParams;
              state.outagesState.loading = false;
            } else {
              state.outagesState.feedbackMessage = {
                message: action.payload?.error?.message || 'Failed to report outage',
                isError: true,
                open: true,
              };
            }
            state.outagesState.loading = false;
          }
        },
        resetOutageFeedbackMessage: (state, action) => {
          state.outagesState.feedbackMessage = {
            message: null,
            open: false,
            isError: false,
          };
          state.outagesState.showOutageDetails = false;
        },
        setShowOutageDetails: (state, action: PayloadAction<boolean>) => {
          state.outagesState.showOutageDetails = action.payload;
        },
        searchOutages: (state, action) => {
          if (action.payload.methodRequestState === 'requested') {
            state.outagesState.searchLoading = true;
            state.outagesState.searchError = null;
          } else if (action.payload.methodRequestState === 'finished') {
            if (action.payload.result) {
              state.outagesState.searchResults = action.payload.result || [];
            } else {
              state.outagesState.searchError = action.payload.error?.message || 'Failed to search outages';
            }
            state.outagesState.searchLoading = false;
          }
        },
        clearSearchResults: (state) => {
          state.outagesState.searchResults = [];
          state.outagesState.searchError = null;
        },
    },
});

export const {
  setOutages,
  setLoading,
  setInputErrors,
  setOutageParams,
  submitOutage,
  resetOutageFeedbackMessage,
  setResetOutageParams,
  setShowOutageDetails,
  searchOutages,
  clearSearchResults,
} = outagesSlice.actions;

export const submitOutageAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
    type: submitOutage.type,
    payload: {
      params,
      method,
    }
  });

export const searchOutagesAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: searchOutages.type,
  payload: {
    params,
    method,
  }
});

export default outagesSlice.reducer;

export const selectOutagesState = (state: RootState) => ({
  ...state.outages.outagesState,
  loading: state.outages.outagesState.searchLoading || state.outages.outagesState.loading,
  error: state.outages.outagesState.searchError,
  searchResults: state.outages.outagesState.searchResults || [],
});
